import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { ChevronRight, ShieldCheck } from "lucide-react";
import ParticleField from "@/components/ParticleField";
import { SecurityIllustration } from "@/components/illustrations/FintechIllustrations";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo {...getSeoForPath("/privacy")} />
      {/* HERO — matches the inner-page style used across the site */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />

        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />

        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          {/* breadcrumbs */}
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Privacy Policy</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            {/* LEFT — text */}
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                <ShieldCheck className="w-3 h-3 text-[var(--swipe-accent)]" />
                Legal &amp; Compliance
              </div>
              <h1 style={{ color: "#FFFFFF" }} className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] leading-[1.05] font-extrabold tracking-[-0.02em]">
                Privacy <span className="text-[var(--swipe-accent)]">Policy</span>
              </h1>
              <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                How Swipetronics collects, uses, and protects information from the business owners we work with.
              </p>
              <p className="mt-5 text-[13px] text-white/55">
                Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>

            {/* RIGHT — illustration */}
            <div className="relative">
              <SecurityIllustration
                transparent
                className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust intro */}
      <section className="bg-[var(--swipe-soft-blue)] border-b border-slate-200/60">
        <div className="container-swipe py-10">
          <div className="mx-auto max-w-3xl flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
              <ShieldCheck className="w-5 h-5 text-[var(--swipe-accent)]" />
            </div>
            <p className="text-[15px] leading-[1.75] text-slate-700">
              Swipetronics is committed to protecting the privacy and confidentiality of the business owners we work with. Any information you submit through our website is used only to review your request, prepare service recommendations, communicate with you, and improve our services. <strong className="text-[var(--swipe-navy)]">We do not sell your personal information.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-swipe py-16">
        <article className="mx-auto max-w-3xl text-[15px] leading-[1.8] text-slate-700">
          <LegalH2>1. Who we are</LegalH2>
          <p>Swipetronics (&quot;Swipetronics&quot;, &quot;we&quot;, &quot;us&quot;) provides merchant processing and point-of-sale solutions. This policy explains what information we collect when you visit <a href="https://swipetronics.com" className="text-[var(--swipe-accent)] underline">swipetronics.com</a> or submit a form on our site, and how we use it.</p>

          <LegalH2>2. Information we collect</LegalH2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Information you provide.</strong> Contact, business, processing volume, and POS information you submit via our savings-analysis, contact, quote, get-started, or ad funnel forms.</li>
            <li><strong>Automatic data.</strong> Standard log data (IP, browser type, referring URL, pages viewed) and cookies/pixels used for analytics and ad attribution (see Section 5).</li>
          </ul>

          <LegalH2>3. How we use information</LegalH2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To review your business, prepare a savings analysis or quote, and contact you about Swipetronics services.</li>
            <li>To operate, secure, and improve the site.</li>
            <li>To measure the performance of our advertising and the effectiveness of our landing pages.</li>
          </ul>
          <p className="mt-4">
            <strong>Contact permission.</strong> By submitting a form, you agree that Swipetronics may contact you by phone, text message, or email about your request. Message and data rates may apply.
          </p>

          <LegalH2>4. Who we share it with</LegalH2>
          <p>We do not sell your personal information. We share information only with service providers acting on our behalf (for example, our database host, our email delivery provider, and analytics/ads platforms we use) and only as needed to deliver the services described above. We may also disclose information when required by law.</p>

          <LegalH2>5. Cookies, pixels, and ad attribution</LegalH2>
          <p>We may use advertising tools such as <strong>Meta Pixel</strong> and <strong>Google Ads</strong> tags to measure ad performance, improve our marketing, and show relevant ads. These tools may set cookies and share limited event data (such as &quot;form submitted&quot;) with Meta and Google. You can opt out of personalized ads via your <a href="https://www.facebook.com/adpreferences" className="text-[var(--swipe-accent)] underline" target="_blank" rel="noreferrer">Meta ad preferences</a> and <a href="https://adssettings.google.com" className="text-[var(--swipe-accent)] underline" target="_blank" rel="noreferrer">Google Ads settings</a>.</p>

          <LegalH2>6. Data retention &amp; security</LegalH2>
          <p>We retain leads for as long as needed to provide our services and meet our legal obligations, then delete them. We apply commercially reasonable safeguards to protect your information, but no online transmission or storage is 100% secure.</p>

          <LegalH2>7. Your choices</LegalH2>
          <p>You may request access to, correction of, or deletion of your personal information by emailing <a href="mailto:info@swipetronics.com" className="text-[var(--swipe-accent)] underline">info@swipetronics.com</a>. You can also unsubscribe from marketing emails at any time using the link in those emails.</p>

          <LegalH2>8. Children</LegalH2>
          <p>The site is intended for business owners and is not directed to children under 13. We do not knowingly collect information from children.</p>

          <LegalH2>9. Changes</LegalH2>
          <p>We may update this policy from time to time. The &quot;Last updated&quot; date above will reflect the latest revision.</p>

          <LegalH2>10. Contact</LegalH2>
          <p>Questions about this policy? Email <a href="mailto:info@swipetronics.com" className="text-[var(--swipe-accent)] underline">info@swipetronics.com</a> or call <a href="tel:+18777947369" className="text-[var(--swipe-accent)] underline">(877) 794-7369</a>.</p>
        </article>
      </section>
    </div>
  );
}

function LegalH2({ children }) {
  return (
    <h2 className="font-display-tight text-2xl font-extrabold text-[var(--swipe-navy)] mt-10 mb-3">
      {children}
    </h2>
  );
}
